import { TFunction } from 'i18next'

import { IStatusTariff, ITariffStatuses } from 'shared/api/TariffApi'
import { Baggage } from 'shared/models/ticket'
import { AllIcons } from 'shared/ui/app-icon/types'

import { RenderIconAvaliable } from '../../render-icon-avaliable'

export interface IGetStatus {
  prefix: string
  status: ITariffStatuses
  includedText: string
  notIncludedText: string
  additionalPaymentText: string
  noInfoText: string
  icon: AllIcons
  disabledIcon: AllIcons
  info: string | undefined
  penalty: string
}

export enum RefundExchangeList {
  REFUND = 'refund',
  EXCHANGE = 'exchange',
}

const getStatusInfoNew = (
  data: IGetStatus,
  t: TFunction<'translation', undefined>,
) => {
  const {
    status,
    prefix,
    includedText,
    notIncludedText,
    additionalPaymentText,
    noInfoText,
    icon,
    disabledIcon,
    info,
    penalty,
  } = data

  const value = (() => {
    switch (status) {
      case ITariffStatuses.NOT_INCLUDED:
      case ITariffStatuses.NOT_AVAILABLE:
        return t(notIncludedText)
      case ITariffStatuses.ADDITIONAL_PAYMENT:
        return t(additionalPaymentText)
      case ITariffStatuses.NO_INFO:
        return t(noInfoText)
      case ITariffStatuses.PENALTY:
        return t(penalty)
      default:
        return t(includedText)
    }
  })()

  const isDisabled =
    status === ITariffStatuses.NOT_INCLUDED ||
    status === ITariffStatuses.NOT_AVAILABLE ||
    status === ITariffStatuses.NO_INFO

  return {
    label: t(prefix),
    value,
    icon: (
      <RenderIconAvaliable
        name={isDisabled ? disabledIcon : icon}
        isActive={!isDisabled}
      />
    ),
    info,
  }
}

export const getСBaggageInfoNew = (
  isBaggage: IStatusTariff,
  baggage: Baggage | null,
  t: TFunction<'translation', undefined>,
) => {
  const includedText =
    baggage?.piece && baggage?.weight
      ? t('tariff_statuses.baggage', {
          piece: baggage.piece,
          weight: baggage.weight,
        })
      : 'tariff_statuses.included_meal'

  const preparedData = {
    prefix: 'hand_baggage',
    status: isBaggage.status,
    includedText,
    notIncludedText: 'tariff_statuses.not_included_luggage',
    additionalPaymentText: 'tariff_statuses.additional_pay_luggage',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECK_ROUNDED,
    disabledIcon: AllIcons.CROSS_BOLDER,
    info: isBaggage.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}

export const getBaggageInfoNew = (
  isBaggage: IStatusTariff,
  baggage: Baggage | null,
  t: TFunction<'translation', undefined>,
) => {
  const includedText =
    baggage?.piece && baggage?.weight
      ? t('tariff_statuses.baggage', {
          piece: baggage.piece,
          weight: baggage.weight,
        })
      : 'tariff_statuses.included_meal'
  const preparedData = {
    prefix: 'baggage',
    status: isBaggage.status,
    includedText,
    notIncludedText: 'tariff_statuses.not_included',
    additionalPaymentText: 'tariff_statuses.additional_pay',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECK_ROUNDED,
    disabledIcon: AllIcons.CROSS_BOLDER,
    info: isBaggage.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}

export const getRefundInfoNew = (
  refund: IStatusTariff,
  t: TFunction<'translation', undefined>,
) => {
  const preparedData = {
    prefix: 'refund',
    status: refund.status,
    includedText: 'tariff_statuses.paid',
    notIncludedText: 'tariff_statuses.not_available',
    additionalPaymentText: 'tariff_statuses.additional_pay',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECK_ROUNDED,
    disabledIcon: AllIcons.CROSS_BOLDER,
    info: refund.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}

export const getExchangeInfoNew = (
  exchange: IStatusTariff,
  t: TFunction<'translation', undefined>,
) => {
  const preparedData = {
    prefix: 'exchange',
    status: exchange.status,
    includedText: 'tariff_statuses.paid',
    notIncludedText: 'tariff_statuses.not_available',
    additionalPaymentText: 'tariff_statuses.additional_pay',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECK_ROUNDED,
    disabledIcon: AllIcons.CROSS_BOLDER,
    info: exchange.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}

export const getMeatInfoNew = (
  meat: IStatusTariff,
  t: TFunction<'translation', undefined>,
) => {
  if (!meat) {
    return null
  }
  const preparedData = {
    prefix: 'meal',
    status: meat.status,
    includedText: 'tariff_statuses.included_meal',
    notIncludedText: 'tariff_statuses.not_included_meal',
    additionalPaymentText: 'tariff_statuses.additional_pay_meal',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECKED_MY_ORDERS,
    disabledIcon: AllIcons.CROSS_NEW,
    info: meat.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}

export const getSeatInfoNew = (
  seat: IStatusTariff,
  t: TFunction<'translation', undefined>,
) => {
  if (!seat) {
    return null
  }
  const preparedData = {
    prefix: 'select_seat',
    status: seat.status,
    includedText: 'tariff_statuses.included_meal',
    notIncludedText: 'tariff_statuses.not_included_meal',
    additionalPaymentText: 'tariff_statuses.additional_pay_meal',
    noInfoText: 'tariff_statuses.no_info',
    icon: AllIcons.CHECKED_MY_ORDERS,
    disabledIcon: AllIcons.CROSS_NEW,
    info: seat.info,
    penalty: 'tariff_statuses.penalty',
  }
  return getStatusInfoNew(preparedData, t)
}
